'use client';

import type { Session } from 'next-auth';
import { useRouter } from 'next/navigation';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { object, string } from 'yup';
import { useT } from '@/libs/i18n/utils/useT';
import { formatErrorsToArray } from '@/modules/Auth/utils/format';
import {
  passwordRequirements,
  passwordSchema,
} from '@/modules/Auth/utils/passwordSchema';
import { useSendOTP } from '@/modules/Customer/http/client/mutations/useSendOTP';
import { useValidateOTP } from '@/modules/Customer/http/client/mutations/useValidateOtp';

const PASSWORD_REQUIREMENT_TYPES = passwordRequirements.map(
  ({ messageKey }) => messageKey
);

export const useResetPassword = (session: Session | null) => {
  const { back } = useRouter();
  const t = useT();

  const {
    mutateAsync: sendOTP,
    isError: isSendOtpError,
    isPending: isSendOtpPending,
  } = useSendOTP();
  const { mutateAsync: validateOtp, isPending: isValidatingOtp } =
    useValidateOTP();

  const identifier = session!.user.document || session!.user.email;

  const [step, setStep] = useState(1);
  const [sentOTPType, setSentOTPType] = useState<'email' | 'phone'>('email');
  const [resetOtp, setResetOtp] = useState('');
  const [errorReset, setErrorReset] = useState<string | null>(null);
  const [isOtpInvalid, setIsOtpInvalid] = useState(false);

  const validationSchema = object().shape({
    password: passwordSchema(t),
    confirmPassword: string()
      .required(
        t('attributes.confirmPassword') +
          ' ' +
          t('formValidation.fieldRequired')
      )
      .test(
        'formValidation.passNotMatch',
        t('formValidation.passNotMatch'),
        function (value) {
          if (!value) return true;

          return value === this.parent.password;
        }
      ),
  });

  const {
    handleSubmit,
    formState: { errors, isSubmitting },
    control,
    reset,
  } = useForm<ResetPasswordForm>({
    resolver: yupResolver(validationSchema),
    reValidateMode: 'onChange',
  });

  const handleSendOTP = async (type: 'email' | 'phone', resend = false) => {
    await sendOTP(
      { identifier: identifier ?? '', otp_type: type },
      {
        onSuccess: () => {
          if (resend) return;
          setSentOTPType(type);
          setStep((prev) => prev + 1);
        },
      }
    );
  };

  const validateOTP = async (token: string) => {
    if (token.length < 6) return;

    const { is_valid } = await validateOtp({
      identifier,
      token,
    });

    if (!is_valid) return setIsOtpInvalid(true);

    setResetOtp(token);
    setStep((prev) => prev + 1);
  };

  const handleBackButton = () => {
    if (step === 2) {
      setStep((prev) => prev - 1);
      setIsOtpInvalid(false);
      return;
    }

    return back();
  };

  const fieldErrorMessage = useMemo(() => {
    const errorsArray = formatErrorsToArray(errors)
      .filter(({ type }) => !PASSWORD_REQUIREMENT_TYPES.includes(type))
      .map(({ message }) => message);

    if (!errorsArray.length) return null;

    return `<p class='otp__error__msg'><b>Erros de validação</b>: <br/> ${errorsArray.join(
      '<br>'
    )}</p>`;
  }, [errors]);

  return {
    control,
    errorReset: errorReset ?? fieldErrorMessage,
    isSendOtpError,
    isSendOtpPending,
    isOtpInvalid,
    isValidatingOtp,
    loading: isSubmitting,
    resetOtp,
    sentOTPType,
    step,
    handleBackButton,
    handleSendOTP,
    handleSubmit,
    reset,
    setErrorReset,
    setResetOtp,
    setStep,
    validateOTP,
  };
};

export type ResetPasswordForm = {
  password: string;
  confirmPassword: string;
};
